import { getBrowserId } from "src/utils/browserId";
import { getPushAttribution } from "src/utils/pushAttribution";

/**
 * Pushification Web Push entegrasyonu.
 *
 * Dev ortamında (npm start) otomatik olarak localhost'taki Pushification'e bağlanır.
 * Prod build'de yalnızca REACT_APP_PUSHIFICATION_URL ve REACT_APP_PUSHIFICATION_PUBLIC_KEY
 * env değişkenleri tanımlıysa aktifleşir — tanımlı değilse hiçbir şey yapmaz.
 *
 * İzin akışı iki adımlıdır:
 *   1. Kendi "soft ask" modalımız sorulur (PushPermissionPrompt).
 *   2. Yalnızca kullanıcı "evet" derse tarayıcının native istemi açılır.
 *
 * Native istem doğrudan ilk kullanıcı hareketine bağlanmaz: listicle sayfasında
 * ilk tıklama çoğu zaman ürün CTA'sıdır ve native modal tam o anda açılınca
 * yönlendirmeyi bozar. Bkz. suppressPushPrompt().
 */

const isDev = process.env.NODE_ENV === "test";

const PUSHIFICATION_URL =
  process.env.REACT_APP_PUSHIFICATION_URL || (isDev ? "http://localhost:8080" : "");

const PUSHIFICATION_PUBLIC_KEY =
  process.env.REACT_APP_PUSHIFICATION_PUBLIC_KEY ||
  (isDev ? "pk_SDKwSCfQjvr_97y_rIkoFxPZQgFMJ5FQ" : "");

/** Soft-ask kararının saklandığı localStorage anahtarı. */
const SOFT_ASK_KEY = "Pushification_SoftAsk";

/** "Şimdi değil" dendikten sonra tekrar sorulmadan önce beklenen gün. */
const DECLINE_COOLDOWN_DAYS = 7;

/** "Evet" denip native istem yanıtsız bırakılırsa beklenen gün. */
const ACCEPT_COOLDOWN_DAYS = 3;

/** Modal, sayfa açıldıktan bu kadar ms sonra gösterilir. */
export const SOFT_ASK_DELAY_MS = 8000;

type SoftAskAnswer = "accepted" | "declined";

interface SoftAskState {
  answer: SoftAskAnswer;
  at: number;
}

let sdkReady = false;
let suppressedForSession = false;
let landingReported = false;
let linkedBrowserId: string | null = null;

export function initPushification(): void {
  if (!PUSHIFICATION_URL || !PUSHIFICATION_PUBLIC_KEY) return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  const script = document.createElement("script");
  script.src = PUSHIFICATION_URL + "/sdk.js";
  script.defer = true;
  script.onload = () => {
    const Pushification = (window as any).Pushification;
    if (!Pushification) return;

    Pushification.init({
      apiBase: PUSHIFICATION_URL,
      publicKey: PUSHIFICATION_PUBLIC_KEY,
      visitorId: getBrowserId() || undefined,
      properties: { source: "listicle" },
    });

    // İzin istemi burada AÇILMAZ; soft-ask modalinin "evet" butonuna bağlıdır.
    sdkReady = true;

    // browser_id, SDK yüklenmeden önce yazılmış olabilir; bu durumda interceptor
    // tetiklenmeyeceği için birleştirmeyi burada yapıyoruz.
    linkPushVisitor(getBrowserId());
    reportNotificationLanding(Pushification);
  };
  script.onerror = () => {
    console.warn("[Pushification] SDK yüklenemedi:", PUSHIFICATION_URL + "/sdk.js");
  };
  document.head.appendChild(script);
}

/**
 * Bildirimden gelen ziyaretin sayfaya gerçekten vardığını ve ne kadar sürdüğünü
 * Pushification'a bildirir.
 *
 * Olay bilerek {@code load} anında gönderilir: sayfa boyanmadan çıkan kullanıcı
 * hiç kayıt üretmez. Bildirim tıklaması sayısı ile buradaki kayıt sayısı
 * arasındaki fark, tam olarak içerik görünmeden kaçanların sayısıdır.
 *
 * Olay adı "$" ile başlayamaz; platform bu öneki rezerve etmiş durumda.
 */
function reportNotificationLanding(Pushification: any): void {
  if (landingReported) return;

  const { notificationId, visitorId: pushVisitorId } = getPushAttribution();
  if (!notificationId && !pushVisitorId) return;

  landingReported = true;

  const send = () => {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    const fcp = performance.getEntriesByName("first-contentful-paint")[0];

    Pushification.track("notification_landing", {
      notificationId,
      // Bildirimin hedeflediği ziyaretçi; SDK'nın kendi kimliğinden farklı
      // olabildiği için ayrıca taşınır.
      pushVisitorId,
      browserId: getBrowserId(),
      path: window.location.pathname,
      ttfbMs: nav ? Math.round(nav.responseStart) : null,
      domContentLoadedMs: nav ? Math.round(nav.domContentLoadedEventEnd) : null,
      loadMs: nav ? Math.round(nav.loadEventEnd) : null,
      fcpMs: fcp ? Math.round(fcp.startTime) : null,
      connection: (navigator as any).connection?.effectiveType ?? null,
    });
  };

  if (document.readyState === "complete") send();
  else window.addEventListener("load", send, { once: true });
}

/**
 * Pushification ziyaretçi kimliğini Listecle'ın browser_id'siyle birleştirir.
 *
 * SDK, browser_id henüz localStorage'a yazılmadan yüklenebildiği için geçici bir
 * kimlikle başlayabiliyor. Kalıcı kimlik hazır olur olmaz bu fonksiyon çağrılır;
 * SDK profili sunucuda yeni kimliğe taşır. Tekrar çağrılması zararsızdır — kimlik
 * zaten eşitse SDK hiçbir şey yapmaz.
 */
export function linkPushVisitor(browserId: string | null): void {
  if (!browserId || linkedBrowserId === browserId) return;

  const Pushification = (window as any).Pushification;
  if (!Pushification || typeof Pushification.linkVisitorId !== "function") return;

  linkedBrowserId = browserId;
  Pushification.linkVisitorId(browserId);
}

/**
 * Ürün CTA'sına tıklandığında çağrılır. Kullanıcı offer sayfasına gidiyor;
 * bu noktadan sonra izin modali açmak hem dönüşümü böler hem de anlamsızdır.
 */
export function suppressPushPrompt(): void {
  suppressedForSession = true;
}

/** Soft-ask modali bu ziyarette gösterilmeli mi? */
export function isPushPromptEligible(): boolean {
  if (!PUSHIFICATION_URL || !PUSHIFICATION_PUBLIC_KEY) return false;
  if (suppressedForSession || !sdkReady) return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  if (typeof Notification === "undefined") return false;
  if (!window.isSecureContext) return false;

  // Kullanıcı tarayıcı düzeyinde zaten karar vermişse native istem açılamaz.
  if (Notification.permission !== "default") return false;

  const state = readSoftAskState();
  if (!state) return true;

  const cooldownDays =
    state.answer === "declined" ? DECLINE_COOLDOWN_DAYS : ACCEPT_COOLDOWN_DAYS;
  return Date.now() - state.at > cooldownDays * 24 * 60 * 60 * 1000;
}

/**
 * Native izin istemini açar.
 *
 * Safari (macOS ve iOS) Notification.requestPermission()'ı yalnızca gerçek bir
 * kullanıcı hareketi içinde kabul eder; bu yüzden çağıran taraf bunu doğrudan
 * onClick handler'ının ilk adımı olarak çalıştırmalıdır.
 */
export function requestPushPermission(): Promise<string> {
  const Pushification = (window as any).Pushification;
  if (!Pushification || typeof Pushification.requestPermission !== "function") {
    return Promise.resolve("unavailable");
  }
  return Pushification.requestPermission();
}

/** Kullanıcının soft-ask cevabını kalıcı olarak saklar. */
export function recordSoftAskAnswer(answer: SoftAskAnswer): void {
  try {
    const state: SoftAskState = { answer, at: Date.now() };
    localStorage.setItem(SOFT_ASK_KEY, JSON.stringify(state));
  } catch {
    // localStorage kullanılamıyor olabilir (gizli mod); sessizce geç.
  }
}

function readSoftAskState(): SoftAskState | null {
  try {
    const raw = localStorage.getItem(SOFT_ASK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.answer !== "accepted" && parsed?.answer !== "declined") return null;
    if (typeof parsed.at !== "number") return null;
    return parsed as SoftAskState;
  } catch {
    return null;
  }
}
